import React, { createRef, useState, useEffect } from 'react';
import DefaultLayout from 'src/components/Layout/Default';
import withLayout from 'src/components/Layout/WithLayout';
import {
    Button,
    Col,
    Row,
    tokens,
    FileUploadPattern,
    ErrorSummary,
    InPageAlert, ComponentLoader,
} from '@snsw-gel/react';
import {
    ButtonsContainer,
    ActionType,
    scrollToTop,
    useApplicationContext,
} from '@gaas/gaas-react-component-library';
import { useHistory } from 'react-router-dom';
import { InputId, categoryName } from './documentNameCatrgoryMap';
import {gaasFilenameFitting, uploadFile} from 'src/services/file-upload-service';
import { FormSteps } from '../../config/formSteps';
import config from '../../config/config';
import tollReliefConfig from '../../config/TollReliefConfig';
import routes from 'src/routes';
import { HeadingWrapper, PageHeadingWrapper, SpacingWrapper } from 'src/components/Styled';
import {IAttachedDocument} from '../../types/GaasAppState';
import {userInfo} from 'src/utils/sessionStorage';
import { EventData, generateEventData } from 'src/utils/ga-page-tracking';
import {pxToRem} from '@snsw-gel/theming';
import {FeatureToggleName, useBaseFeature} from '../../hooks/features/useBaseFeature';

const decorateFile = (files: IAttachedDocument[], category: string) => {
    return files.map((attachment) => ({
        ...attachment,
        name: gaasFilenameFitting(attachment.name),
        documentType: category
    }));
}

// TODO remove this file after Tr2 decom
const SupportingDocumentsPage: React.FC<{ progressStepper: any }> = () => {
    const history = useHistory();
    const {globalState, dispatch} = useApplicationContext();

    const { isFeatureEnabled, isFeatureQueryLoading } = useBaseFeature();
    const isTr2Decommissioned = !isFeatureQueryLoading && isFeatureEnabled(FeatureToggleName.TR2_DECOMMISSION);

    const errorSummaryRefRow = createRef();
    const {supportingDocuments} = globalState;
    const [uploadError, setUploadError] = useState<any>({});
    const fileExtensions = '.pdf,.png,.jpg,.jpeg';
    const maxMBSize = 8;

    const onBack = () => {
        scrollToTop();
        dispatch({type: ActionType.UPDATE_SUPPORTING_DOCUMENTS_ACTION, payload: {supportingDocuments}});
        history.push(routes.soleTraderBusinessDetails.path);
    }

    const validateForm = () => {
        const uploadErrors = {};
        if (config.appConfig.bypassFileUpload) {
            return uploadErrors;
        }

        if (!(supportingDocuments.documents?.length > 0)) {
            uploadErrors['evidenceOfBusinessTurnover'] = 'Upload evidence of business turnover to continue.';
        }

        // replace whole error based on validation above
        setUploadError(uploadErrors);
        return uploadErrors;
    };

    const handleUpload = (inputId: InputId) => (lastFile) => {
        supportingDocuments.documents = decorateFile([...lastFile], categoryName(inputId));
        clearUploadError(inputId);
    };

    /**
     *  we only have 1 FileUpload/Type, no need to check during fileExist/remove
     */
    const handleRemoveFile = (inputId: InputId) => (updatedListOfFiles) => {
        supportingDocuments.documents = decorateFile([...updatedListOfFiles], categoryName(inputId));
    };

    const fileExists = (filename) => {
        const files = supportingDocuments.documents;
        return files && files.filter(file => filename === file.name)?.length > 0;
    }

    const setDuplicateFileError = (inputId: InputId) => {
        setUploadError({
            ...uploadError,
            [inputId]: 'File already exists'
        });
    }

    const clearUploadError = (inputId: InputId) => {
        if (!supportingDocuments.errors) {
            return; // it's empty nothing todo
        }

        // exclude 'inputId' and keep others in 'otherErrors'
        const {[inputId]: value, ...otherErrors} = supportingDocuments.errors;

        dispatch({
            type: ActionType.UPDATE_SUPPORTING_DOCUMENTS_ACTION,
            payload: {
                supportingDocuments: {
                    ...supportingDocuments,
                    errors: {...otherErrors}
                }
            }
        });
    }

    const uploadApi = (inputId: InputId) => {
        const category = categoryName(inputId);

        return {
            uploadFile: (file) => {
                const fileName = new File([file], gaasFilenameFitting(`${category}_${file.name}`));

                if (fileExists(file.name)) {
                    return setDuplicateFileError(inputId);
                }

                return uploadFile(fileName, fileName.name, category, globalState, config)
                    .catch(error => {
                        if (error === 'The file contents are not valid') {
                            error = 'The file you have attempted to upload is an unsupported file type. You can only upload PDF, PNG, JPG or JPEG files.';
                        }
                        setUploadError({
                            ...uploadError,
                            [inputId]: error
                        });
                    });
            },
            removeFile: () => {
            },
        }
    };

    const onSave = async () => {
        try {
            const errors = validateForm();
            supportingDocuments.errors = errors
            dispatch({type: ActionType.UPDATE_SUPPORTING_DOCUMENTS_ACTION, payload: {supportingDocuments}});

            await scrollToTop();
            if (errors && Object.keys(errors).length) {
                return;
            }

            history.push(routes.ReviewPage.path);
        } catch (err) {
            console.error(err);
        }
    };

    const formErrors = Object.keys(supportingDocuments.errors).map(k => {
        return {
            id: `${tollReliefConfig.anchors[k]}-dropzone`, // All file upload id has suffix '-dropzone'
            text: supportingDocuments.errors[k]
        }
    });

    // Require toll account type to exist
    useEffect(() => {
        if (isTr2Decommissioned) {
            history.push(routes.manageTollAccount.path);
        }
    }, [isTr2Decommissioned]);

    const userInfoObj = userInfo();
    useEffect(() => {
        if (userInfoObj && userInfoObj.id) {
            // Simulating data for the event
            const eventData: EventData = {
                page_title: 'Link toll account',
                transaction_info: {
                    transaction_step: 'Supporting details',
                },
                user_info: {
                    user_id: userInfoObj.id,
                    user_login_status: 'fully authenticated',
                },
            };

            // Generating and pushing the event data to the dataLayer
            generateEventData(eventData);
        }
    }, [userInfoObj]);

    return (
        <>
            <ComponentLoader active={isFeatureQueryLoading ||
                isTr2Decommissioned} fullPage
                             label={tollReliefConfig.labels.componentLoader}/>

            <Row ref={errorSummaryRefRow} id={formErrors.length ? 'supportingDocError' : undefined}>
                <Col span={8}>
                    {!!formErrors.length && <ErrorSummary errors={formErrors}/>}
                </Col>
            </Row>

            <Row>
                <Col span={8}>
                    <SpacingWrapper>All fields to be completed unless marked (optional)</SpacingWrapper>
                    <PageHeadingWrapper>Supporting details</PageHeadingWrapper>
                </Col>
            </Row>

            <Row>
                <Col span={8}>
                    <InPageAlert variant='info' title='Conceal your tax file number'>
                        <p>We recommend concealing your tax file number before uploading documents.
                            You are not required to show your tax file number for this claim.</p>
                    </InPageAlert>
                </Col>
            </Row>

            <Row>
                <Col span={8}>
                    <HeadingWrapper>Evidence of business turnover</HeadingWrapper>

                    <p>Upload evidence of the business’ aggregated annual turnover. Please provide:</p>
                    <ul>
                        <li>an Australian Income Tax Return for the 2022-23 or 2021-22 financial year, or</li>
                        <li>consecutive BAS or IAS statements for the 2023-24 financial year.</li>
                    </ul>
                    <p><strong>Note:</strong> Registered charities can provide their ACNC Annual Information Statement (AIS).</p>

                    <FileUploadPattern
                        name='evidenceOfBusinessTurnover'
                        id={tollReliefConfig.anchors['evidenceOfBusinessTurnover']}
                        category='BusinessTurnover'
                        legendText=''
                        maxMBSize={maxMBSize}
                        maxFiles={5}
                        api={uploadApi('evidenceOfBusinessTurnover')}
                        onUploaded={handleUpload('evidenceOfBusinessTurnover')}
                        onRemoved={handleRemoveFile('evidenceOfBusinessTurnover')}
                        initialFiles={supportingDocuments.documents}
                        acceptedFormats={fileExtensions}
                        style={{maxWidth: '450px'}}
                        errorMessage={uploadError['evidenceOfBusinessTurnover']}
                    />

                </Col>
            </Row>

            <ButtonsContainer>
                <Button
                    className='button-item'
                    id='nextButton'
                    onClick={onSave}
                    data-testid='submitButton'
                >
                    Continue
                </Button>
            </ButtonsContainer>
            <Button
                style={{marginTop: `${pxToRem(tokens.spacing.sm)}`}}
                id='backButton'
                onClick={onBack}
                theme='back'
            >
                Back
            </Button>
        </>
    )
}

export default withLayout(SupportingDocumentsPage, DefaultLayout, {
    step: FormSteps.SupportingDocuments,
    headerTitle: 'Link toll account',
});
